import React, { useMemo } from "react";
import BaseChart from "../../components/ui/BaseChart";
import ChartPanel from "./ChartPanel";

// Shared palette with the rest of the dashboard so a chart never invents a colour.
const PALETTE = [
  "#0F40AE",
  "#0891B2",
  "#16A34A",
  "#f7ad00",
  "#7C3AED",
  "#DC2626",
  "#EC4899",
  "#84CC16",
];

/**
 * One reusable breakdown panel — bar or donut — for the simple
 * "label → count" series (locations, categories, payment types, invoice
 * statuses). Keeps those four panels visually identical instead of drifting
 * across four near-copies.
 */
const BreakdownChart = ({
  title,
  subtitle,
  icon,
  accent,
  accentBg,
  type = "bar",
  help,
  data = [],
  height = 300,
  emptyMessage,
  isLoading,
  error,
}) => {
  const options = useMemo(() => {
    if (type === "pie") {
      return {
        chart: { type: "pie", height },
        title: { text: null },
        tooltip: { pointFormat: "<b>{point.y}</b> ({point.percentage:.1f}%)" },
        plotOptions: {
          pie: {
            innerSize: "62%",
            borderWidth: 0,
            dataLabels: {
              enabled: true,
              format: "{point.name}: {point.y}",
              style: {
                fontSize: "11px",
                fontWeight: "400",
                textOutline: "none",
                color: "#6B7280",
              },
            },
          },
        },
        series: [
          {
            name: title,
            data: data.map((row, index) => ({
              name: row.label,
              y: row.value,
              color: row.color ?? PALETTE[index % PALETTE.length],
            })),
          },
        ],
      };
    }

    return {
      chart: { type: "bar", height },
      title: { text: null },
      legend: { enabled: false },
      xAxis: {
        categories: data.map((row) => row.label),
        labels: { style: { fontSize: "11px", color: "#6B7280" } },
        lineColor: "#E5E7EB",
      },
      yAxis: {
        title: { text: null },
        allowDecimals: false,
        gridLineColor: "#F3F4F6",
        labels: { style: { fontSize: "11px", color: "#9CA3AF" } },
      },
      tooltip: {
        formatter() {
          const row = data[this.point.index];
          const suffix = row?.note ? `<br/><span style="color:#9CA3AF">${row.note}</span>` : "";
          return `<b>${this.x}</b>: ${this.y}${suffix}`;
        },
      },
      plotOptions: { bar: { borderWidth: 0, borderRadius: 3, colorByPoint: true } },
      colors: PALETTE,
      series: [{ name: title, data: data.map((row) => row.value) }],
    };
  }, [type, data, height, title]);

  return (
    <ChartPanel
      title={title}
      subtitle={subtitle}
      icon={icon}
      accent={accent}
      accentBg={accentBg}
      help={help}
      isLoading={isLoading}
      error={error}
      isEmpty={data.length === 0}
      emptyMessage={emptyMessage}
    >
      <BaseChart options={options} />
    </ChartPanel>
  );
};

export default BreakdownChart;
